import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePortal } from "../context/PortalContext";
import clinicAPI from "../services/clinicApi";
import { updateTenant } from "../store/authSlice";
import {
  applyPortalBrandingToRoot,
  applyPortalThemeMode,
  buildClientHeroTheme,
  clubBrandingSource,
  heroBackgroundStyle,
  mergeClubPortalBranding,
  portalGlobalCssVars,
  resolvePortalThemeMode,
} from "../utils/clientHeroTheme";
import { resolvePlanAccess } from "../utils/planEntitlements";

function mergeTenantBranding(tenant, clinic) {
  if (!tenant && !clinic) return null;
  const t = tenant || {};
  const c = clinic || {};
  return {
    ...t,
    ...c,
    id: t.id ?? c.id,
    name: t.name ?? c.name,
    subdomain: t.subdomain ?? c.subdomain,
    logo_url: t.logo_url ?? c.logo_url,
    portal_accent_color: t.portal_accent_color ?? c.portal_accent_color,
    portal_cover_url: t.portal_cover_url ?? c.portal_cover_url,
    portal_font_family: t.portal_font_family ?? c.portal_font_family,
    portal_theme_mode: t.portal_theme_mode ?? c.portal_theme_mode,
    portal_gallery: t.portal_gallery ?? c.portal_gallery,
  };
}

function pickBrandingFields(source) {
  if (!source) return null;
  return {
    portal_accent_color: source.portal_accent_color,
    portal_cover_url: source.portal_cover_url,
    portal_font_family: source.portal_font_family,
    portal_theme_mode: source.portal_theme_mode,
    name: source.name,
    logo_url: source.logo_url,
  };
}

/**
 * Единая персонализация клуба: фирменный цвет, обложка шапки, шрифт, тема.
 * @param {"aa"|"ca"} cssPrefix — админ или клиентское приложение
 * @param {object|null} extraBranding — например trainer_branding с dashboard
 * @param {boolean} refreshFromApi — подтянуть свежие настройки с сервера
 * @param {boolean} active — применять брендинг (false на загрузочном экране)
 */
export default function useClubPortalBranding(
  cssPrefix = "aa",
  extraBranding = null,
  refreshFromApi = false,
  active = true,
) {
  const dispatch = useDispatch();
  const { clinic } = usePortal();
  const tenant = useSelector((state) => state.auth.tenant);
  const { entitlements, billing } = useSelector((state) => state.auth);
  const tenantBranding = useMemo(
    () => mergeTenantBranding(tenant, clinic),
    [tenant, clinic],
  );
  const plan = useMemo(
    () => resolvePlanAccess(entitlements, tenantBranding, billing),
    [entitlements, tenantBranding, billing],
  );

  useEffect(() => {
    if (!refreshFromApi) return undefined;
    let cancelled = false;

    const applyFields = (source) => {
      const fields = pickBrandingFields(source);
      if (cancelled || !fields) return;
      dispatch(updateTenant(fields));
    };

    clinicAPI
      .getPublicClinicPortal()
      .then((res) => applyFields(res?.clinic))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [refreshFromApi, dispatch]);

  const branding = useMemo(() => {
    let source =
      mergeClubPortalBranding(tenantBranding, extraBranding) ||
      clubBrandingSource(tenantBranding);
    const canBrand = plan.fullBranding || plan.brandingLite;
    if (source && !canBrand) {
      source = {
        ...source,
        portal_accent_color: null,
        portal_cover_url: null,
        portal_font_family: null,
        portal_theme_mode: "light",
        portal_gallery: [],
      };
    } else if (source && plan.brandingLite && !plan.fullBranding) {
      source = {
        ...source,
        portal_cover_url: null,
        portal_gallery: [],
      };
    }
    return source;
  }, [
    tenantBranding,
    extraBranding,
    plan.fullBranding,
    plan.brandingLite,
    tenantBranding?.portal_accent_color,
    tenantBranding?.portal_cover_url,
    tenantBranding?.portal_font_family,
    tenantBranding?.portal_theme_mode,
    extraBranding?.portal_accent_color,
    extraBranding?.portal_cover_url,
    extraBranding?.portal_font_family,
    extraBranding?.portal_theme_mode,
  ]);

  const themeMode = useMemo(
    () => resolvePortalThemeMode(branding || tenantBranding),
    [branding, tenantBranding?.portal_theme_mode],
  );

  const theme = useMemo(() => buildClientHeroTheme(branding), [branding]);

  const shellStyle = useMemo(() => portalGlobalCssVars(theme), [theme]);

  const heroStyle = useMemo(() => heroBackgroundStyle(theme), [theme]);

  useEffect(() => {
    if (!active) return undefined;
    applyPortalBrandingToRoot(theme);
    applyPortalThemeMode(themeMode);
    return undefined;
  }, [theme, themeMode, active]);

  return { theme, branding, shellStyle, heroStyle, themeMode };
}
